import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

export const UniqueServiceNameInput = ({
  onChange,
  value,
  required,
  rawErrors,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [services, setServices] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>(value || '');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const fetchServices = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/service`);

      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.statusText}`);
      }

      const serviceList = await response.json();
      setServices(
        Array.isArray(serviceList)
          ? serviceList.map((s: string) => s.trim().toLowerCase())
          : [],
      );
      setFetchError(null);
    } catch (err) {
      setFetchError('Failed to load existing services for uniqueness check.');
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.trim().toLowerCase();
    setInputValue(newValue);
    onChange(newValue);

    const normalized = newValue;
    if (normalized && services.includes(normalized)) {
      setDuplicateError(`Service "${normalized}" already exists. Choose a unique name.`);
    } else {
      setDuplicateError(null);
    }
  };

  const hasError = !!duplicateError || !!fetchError || (rawErrors && rawErrors.length > 0);

  return (
    <FormControl margin="normal" required={required} error={hasError} fullWidth>
      <TextField
        label={schema.title || 'Service Name'}
        value={inputValue}
        onChange={handleChange}
        required={required}
        error={hasError}
        helperText={
          duplicateError ||
          fetchError ||
          schema.description ||
          'Enter a unique service name using only lowercase letters, numbers, and hyphens'
        }
        placeholder="e.g. identity-provider"
        inputProps={{
          pattern: '^[a-z0-9-]+$',
        }}
      />
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};
